/**
 * **Cetak X-report / Z-report** (Imron, 2026-08-22).
 *
 * ⛔ Bentuknya sengaja MENIRU `PrintButton`, termasuk keadaannya: `ok` · `recorded` · `error`.
 * Alasannya sama — "byte tak bisa dibuat" (perangkat tanpa profil printer) berbeda dari "gagal
 * mengirim ke printer", dan kasir yang tak bisa membedakan keduanya akan menekan tombolnya
 * berulang kali sampai kertasnya habis (K27).
 *
 * ⚠️ Angka rekapnya ikut ditampilkan walau printernya mati. Lembar ini dipakai menghitung uang;
 * tak bisa mencetak bukan alasan untuk tak bisa MEMBACA.
 */
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { printViaAgent } from '../api/agent';
import { api } from '../api/client';
import type { ShiftReportJobResponse } from '../api/client';

type Status =
  | { kind: 'idle' }
  | { kind: 'busy' }
  | { kind: 'ok' }
  | { kind: 'recorded'; message: string }
  | { kind: 'error'; message: string };

export interface ShiftReportButtonProps {
  readonly shiftId: string;
  readonly kind: 'x' | 'z';
}

export function ShiftReportButton({ shiftId, kind }: ShiftReportButtonProps) {
  const { t } = useTranslation();
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [doc, setDoc] = useState<ShiftReportJobResponse['doc'] | null>(null);

  const cetak = async (): Promise<void> => {
    setStatus({ kind: 'busy' });
    let job: ShiftReportJobResponse | null | undefined;
    try {
      job = (await api.shiftReport(shiftId, kind)).job;
    } catch (e: unknown) {
      setStatus({ kind: 'error', message: (e as Error).message });
      return;
    }
    if (job === undefined || job === null) {
      /* ⚠️ Bukan galat: rekapnya ADA, cuma perangkat ini tak punya jalur cetak. */
      setStatus({ kind: 'recorded', message: t('session.report.noDevice') });
      return;
    }
    setDoc(job.doc);
    const kirim = await printViaAgent({ bytesBase64: job.bytesBase64, address: job.address });
    setStatus(kirim.ok
      ? { kind: 'ok' }
      : { kind: 'recorded', message: `${kirim.code ?? ''} ${kirim.message ?? ''}`.trim() });
  };

  return (
    <div className="print-box" data-testid={`report-box-${kind}`}>
      <button
        type="button"
        className="action-secondary"
        data-testid={`print-report-${kind}`}
        disabled={status.kind === 'busy'}
        onClick={() => void cetak()}
      >
        {status.kind === 'busy'
          ? t('common.loading')
          : t(kind === 'x' ? 'session.report.x' : 'session.report.z')}
      </button>

      {status.kind === 'ok' && <p className="ok" data-testid={`report-ok-${kind}`}>{t('session.report.sent')}</p>}
      {status.kind === 'recorded' && (
        <p className="warn" data-testid={`report-recorded-${kind}`}>{status.message}</p>
      )}
      {status.kind === 'error' && (
        <p className="error" data-testid={`report-error-${kind}`}>{status.message}</p>
      )}

      {/* ⭐ Angkanya terbaca walau tak tercetak — lembar ini dipakai menghitung uang. */}
      {doc !== null && (
        <dl className="admin-info" data-testid={`report-doc-${kind}`}>
          <dt>{t('session.report.sales')}</dt>
          <dd>{doc.sales.grandTotal}</dd>
          <dt>{t('session.report.expected')}</dt>
          <dd>{doc.cash.expected}</dd>
        </dl>
      )}
    </div>
  );
}
